/* MD Cajón del carrito: apertura y cierre del <dialog>, foco, bloqueo del scroll, apertura
   automática al añadir al carrito, "Vaciar carrito" y scroll arriba tras añadir un sugerido.

   Es un módulo (no una IIFE) porque extiende la clase base de componentes del tema: el disparador
   de la cabecera usa on:click="#cart-drawer/toggle" y los botones internos on:click="#cart-drawer/close",
   y ese sistema de eventos declarativos solo llama a métodos de instancias de esa clase.

   El <dialog> se abre siempre en modo modal (showModal): el navegador aporta el scrim (::backdrop,
   estilado en la hoja), el foco atrapado, el cierre con Escape (evento "cancel") y deja inerte el
   resto de la página. Aquí se añade lo que el navegador no hace: bloquear el scroll de fondo,
   animar la salida, cerrar al pulsar el scrim, devolver el foco al disparador, sincronizar
   aria-expanded en los disparadores y abrir el cajón al añadir un producto.

   El contenido (líneas, cantidades, eliminar, cupones, estado vacío) lo repintan los componentes
   del tema (component-cart-items.js, cart-discount.js) sin tocar este elemento ni el <dialog>.
   "Vaciar carrito" replica el disparo de component-cart-items.js: emite el evento estándar de
   líneas con una promesa, hace la petición pidiendo esta sección y repinta con morphSection. */
import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig, lockScroll, unlockScroll, onAnimationEnd, isClickedOutside } from '@theme/utilities';

const CLOSING_CLASS = 'md-cart-drawer__panel--closing';
const CLOSE_TIMEOUT_MS = 600;
const TRIGGER_SELECTOR = '[aria-controls="cart-drawer"]';
const CLEAR_SELECTOR = '.md-cart-drawer__vaciar';
const CLEAR_CONFIRM_ATTR = 'data-md-confirm';
const SCROLLER_SELECTOR = '.md-cart-drawer__body';
const SOLO_DOMICILIO_SELECTOR = '.md-cart-drawer__sellos[data-md-solo-domicilio]';
// Atributos del carrito donde Globe guarda la elección del selector (domicilio o punto de recogida)
const GLOBE_ATTRIBUTES = ['globe_DeliveryMethod', 'globe_PickupPointData'];

/**
 * Un elemento oculto (display: none en la cabecera móvil o de escritorio) no puede recibir el foco.
 * @param {Element} element
 * @returns {boolean}
 */
function isVisible(element) {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility();
  }
  return element instanceof HTMLElement && element.offsetParent !== null;
}

/**
 * Cancela la acción por defecto del evento que dispara open/close/toggle (si lo hay).
 * @param {unknown} event
 */
function cancelDefault(event) {
  if (event && typeof event === 'object' && 'preventDefault' in event && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
}

/** @returns {boolean} */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel
 * @property {HTMLButtonElement} [closeButton]
 *
 * @extends {Component<Refs>}
 */
class MdCartDrawerComponent extends Component {
  requiredRefs = ['panel'];

  /** @type {boolean} */
  #closing = false;

  /** @type {boolean} */
  #clearing = false;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @returns {boolean} */
  get isOpen() {
    return this.refs.panel.open && !this.#closing;
  }

  connectedCallback() {
    super.connectedCallback();
    const { panel } = this.refs;

    panel.addEventListener('cancel', this.#onCancel);
    panel.addEventListener('click', this.#onPanelClick);
    panel.addEventListener('close', this.#onClosed);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.#onSectionDeselect);
    }

    this.#syncTriggers(false);
    this.#clearGlobeChoiceIfSoloDomicilio();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { panel } = this.refs;

    panel.removeEventListener('cancel', this.#onCancel);
    panel.removeEventListener('click', this.#onPanelClick);
    panel.removeEventListener('close', this.#onClosed);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.removeEventListener('shopify:section:select', this.#onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);

    unlockScroll(panel);
  }

  /**
   * Abre el cajón como diálogo modal y lleva el foco al botón de cerrar.
   * El sistema de eventos declarativos del tema pasa el evento como primer argumento: se cancela
   * la acción por defecto para que el enlace del carrito de la cabecera (href a /cart, que sigue
   * funcionando sin JavaScript) abra el cajón en vez de navegar.
   * @param {Event} [event]
   */
  open(event) {
    cancelDefault(event);
    const { panel } = this.refs;
    if (panel.open || this.#closing) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    lockScroll(panel);
    panel.showModal();
    this.#focusInitialElement();
    this.#syncTriggers(true);
    this.dispatchEvent(new CustomEvent('md-cart-drawer:open', { bubbles: true }));
  }

  /**
   * Anima la salida y cierra el diálogo; el resto de la limpieza ocurre en el evento "close".
   * @param {Event} [event]
   */
  async close(event) {
    cancelDefault(event);
    const { panel } = this.refs;
    if (!panel.open || this.#closing) return;
    this.#closing = true;

    panel.classList.add(CLOSING_CLASS);
    // Sin animación (movimiento reducido) la promesa se resuelve al instante. Con la pestaña en
    // segundo plano las animaciones no avanzan y no terminarían nunca: tope de tiempo de seguridad.
    await Promise.race([
      onAnimationEnd(panel, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, CLOSE_TIMEOUT_MS)),
    ]);
    panel.classList.remove(CLOSING_CLASS);

    this.#closing = false;
    panel.close();
  }

  /** @param {Event} [event] */
  toggle(event) {
    cancelDefault(event);
    if (this.refs.panel.open) {
      return this.close();
    }
    return this.open();
  }

  /**
   * "Vaciar carrito", primer paso: muestra la confirmación en línea y lleva el foco al "Sí".
   * @param {Event} [event]
   */
  askClear(event) {
    cancelDefault(event);
    const clear = this.#clearBox();
    if (!clear) return;
    clear.setAttribute(CLEAR_CONFIRM_ATTR, '');
    const yes = clear.querySelector('.md-cart-drawer__vaciar-yes');
    if (yes instanceof HTMLElement) yes.focus();
  }

  /**
   * "Vaciar carrito", cancelar: vuelve al botón inicial.
   * @param {Event} [event]
   */
  cancelClear(event) {
    cancelDefault(event);
    const clear = this.#clearBox();
    if (!clear) return;
    clear.removeAttribute(CLEAR_CONFIRM_ATTR);
    const button = clear.querySelector('.md-cart-drawer__vaciar-btn');
    if (button instanceof HTMLElement) button.focus();
  }

  /**
   * "Vaciar carrito", confirmado: vacía por AJAX pidiendo esta sección, la repinta y emite el
   * evento estándar de líneas con la promesa, como hace el componente de líneas del tema, para que
   * la burbuja de la cabecera y el resto de oyentes se actualicen.
   * @param {Event} [event]
   */
  async clearCart(event) {
    cancelDefault(event);
    if (this.#clearing) return;

    const { panel } = this.refs;
    const sectionId = this.dataset.sectionId;
    const clearUrl = this.dataset.clearUrl;
    if (!sectionId || !clearUrl) return;

    this.#clearing = true;
    const clear = this.#clearBox();
    clear?.setAttribute('aria-busy', 'true');

    const lines = Array.from(panel.querySelectorAll('[ref="cartItemRows[]"]')).map((row) => ({
      id: row instanceof HTMLElement ? row.dataset.key ?? '' : '',
      quantity: 0,
    }));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({ sections: [sectionId], sections_url: window.location.pathname });
      const response = await fetch(clearUrl, fetchConfig('json', { body }));
      const data = await response.json();

      if (!response.ok || data.errors) {
        throw new Error(typeof data.errors === 'string' ? data.errors : 'No se ha podido vaciar el carrito');
      }

      const html = data.sections?.[sectionId];
      if (html) {
        await morphSection(sectionId, html, { mode: 'hydration' });
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          items: data.items,
          itemCount: 0,
          source: 'md-cart-drawer',
          didError: false,
        },
      });

      // El botón pulsado ha desaparecido con el repintado: el foco vuelve al título
      this.#focusHeading();
    } catch (error) {
      deferred.reject(error);
      clear?.removeAttribute(CLEAR_CONFIRM_ATTR);
      console.error(error);
    } finally {
      clear?.removeAttribute('aria-busy');
      this.#clearing = false;
    }
  }

  /**
   * Escape: el navegador lanza "cancel" en el diálogo modal. Se cancela el cierre instantáneo
   * para hacer el animado.
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Clic en el scrim: el objetivo es el propio <dialog> pero el punto cae fuera de su caja.
   * @param {MouseEvent} event
   */
  #onPanelClick = (event) => {
    if (isClickedOutside(event, this.refs.panel)) {
      this.close();
    }
  };

  /** Tras cerrarse el diálogo (por este componente o por el navegador): scroll, foco y disparadores. */
  #onClosed = () => {
    const { panel } = this.refs;
    panel.classList.remove(CLOSING_CLASS);
    this.#closing = false;

    unlockScroll(panel);
    this.#syncTriggers(false);
    this.dispatchEvent(new CustomEvent('md-cart-drawer:close', { bubbles: true }));

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;

    // Un repintado del carrito puede haber sustituido el disparador original: se busca uno vivo.
    // Si el original ya no existe o no está visible (cabecera móvil/escritorio), el primero visible.
    if (trigger && document.contains(trigger) && isVisible(trigger)) {
      trigger.focus({ preventScroll: true });
    } else {
      for (const candidate of document.querySelectorAll(TRIGGER_SELECTOR)) {
        if (candidate instanceof HTMLElement && isVisible(candidate)) {
          candidate.focus({ preventScroll: true });
          break;
        }
      }
    }
  };

  /**
   * Cambios en las líneas del carrito.
   * - "remove": el botón pulsado desaparece del DOM y el foco caería al body, fuera del diálogo.
   *   Cuando termina la petición y la animación de la fila, si el foco ya no está dentro del panel
   *   se lleva al título, que anuncia el nuevo recuento.
   * - "add" desde dentro del cajón (tarjetas de "Completa tu disfraz"): al terminar, la zona con
   *   scroll sube al principio para que se vea la línea nueva.
   * - "add" desde fuera: apertura automática (ajuste del tema "Abrir el cajón al añadir"). Si el
   *   producto se añade desde otro diálogo modal (compra rápida), se espera a que se cierre para
   *   no solapar dos modales.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    // El repintado que pone o quita data-md-solo-domicilio ocurre en otro oyente: se espera un ciclo
    event.promise
      ?.then(() => requestAnimationFrame(() => this.#clearGlobeChoiceIfSoloDomicilio()))
      .catch(() => {});

    if (event.target === this) return;

    if (event.action === 'remove') {
      this.#restoreFocusAfterRemove(event);
      return;
    }

    if (event.action !== 'add') return;

    const { panel } = this.refs;
    const fromInside = event.target instanceof Node && panel.contains(event.target);

    if (fromInside) {
      this.#scrollTopAfterAdd(event);
      return;
    }

    if (!this.hasAttribute('data-auto-open')) return;
    if (panel.open) return;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        if (sourceModal && sourceModal.open) {
          sourceModal.addEventListener('close', () => this.open(), { once: true });
        } else {
          this.open();
        }
      })
      .catch(() => {
        // El error ya lo comunica el formulario que ha añadido el producto
      });
  };

  /**
   * Editor de temas: al seleccionar la sección se abre el cajón para poder verlo.
   * @param {Event} event
   */
  #onSectionSelect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.open();
    }
  };

  /** @param {Event} event */
  #onSectionDeselect = (event) => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.close();
    }
  };

  /**
   * Carrito con productos de Atosa (data-md-solo-domicilio en los sellos, ver la sección): el
   * selector de Globe está oculto, pero el cliente pudo elegir un punto de recogida antes de añadir
   * el producto. Esa elección se borra del carrito para que Globe no rellene en el checkout la
   * dirección del punto ni la guarde en el pedido, que solo puede ir a domicilio.
   */
  async #clearGlobeChoiceIfSoloDomicilio() {
    if (!this.refs.panel.querySelector(SOLO_DOMICILIO_SELECTOR)) return;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await response.json();
      const saved = GLOBE_ATTRIBUTES.filter((name) => cart.attributes?.[name]);
      if (saved.length === 0) return;

      const attributes = Object.fromEntries(saved.map((name) => [name, '']));
      await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body: JSON.stringify({ attributes }) }));
    } catch (error) {
      console.error(error);
    }
  }

  /** @returns {HTMLElement | null} */
  #clearBox() {
    const box = this.refs.panel.querySelector(CLEAR_SELECTOR);
    return box instanceof HTMLElement ? box : null;
  }

  #focusInitialElement() {
    const { closeButton, panel } = this.refs;
    const target = closeButton instanceof HTMLElement ? closeButton : panel.querySelector('.md-cart-drawer__close');
    if (target instanceof HTMLElement) {
      target.focus();
    }
  }

  #focusHeading() {
    const heading = this.refs.panel.querySelector('.md-cart-drawer__title');
    if (heading instanceof HTMLElement) {
      heading.focus({ preventScroll: true });
    } else {
      this.#focusInitialElement();
    }
  }

  /**
   * Tras añadir un sugerido desde el propio cajón: cuando el servidor ha respondido y el cajón se
   * ha repintado, la zona con scroll sube al principio (sin animación si el usuario lo pide).
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #scrollTopAfterAdd(event) {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        // El repintado ocurre en otro oyente de este mismo evento: se espera un ciclo
        requestAnimationFrame(() => {
          const scroller = this.refs.panel.querySelector(SCROLLER_SELECTOR);
          if (!(scroller instanceof HTMLElement)) return;
          scroller.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        });
      })
      .catch(() => {
        // El error ya lo comunica el formulario de la tarjeta
      });
  }

  /**
   * Tras eliminar una línea con el cajón abierto: si el foco se ha perdido (el botón ya no existe),
   * se lleva al título del cajón; si no hay título, al botón de cerrar.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #restoreFocusAfterRemove(event) {
    const { panel } = this.refs;
    if (!panel.open) return;

    const settle = event.promise ? event.promise.catch(() => {}) : Promise.resolve();

    settle
      // La fila se quita al acabar su animación de salida: se espera también a esa animación
      .then(() => {
        const removingRows = Array.from(panel.querySelectorAll('.md-cart-drawer__line.removing'));
        return onAnimationEnd(removingRows, undefined, { subtree: false });
      })
      .then(() => {
        if (!panel.open || this.#closing) return;

        const active = document.activeElement;
        if (active && active !== panel && active !== document.body && panel.contains(active)) return;

        this.#focusHeading();
      });
  }

  /**
   * aria-expanded en los disparadores externos (carrito de la cabecera). Es el único acceso fuera
   * del componente: los disparadores viven en otra sección por diseño.
   * @param {boolean} expanded
   */
  #syncTriggers(expanded) {
    for (const trigger of document.querySelectorAll(TRIGGER_SELECTOR)) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }
}

if (!customElements.get('md-cart-drawer-component')) {
  customElements.define('md-cart-drawer-component', MdCartDrawerComponent);
}
